import { useReactiveVar } from '@apollo/client';
import { clsx } from 'clsx';
import { Suspense, useState } from 'react';
import { PrefetchPageLinks, useLocation, useNavigate } from 'react-router';
import { LayoutCard } from '@/components/layout/card.tsx';
import { getPageMeta } from '@/routes-utils/get-page-meta.ts';
import { invalidateSession } from '@/routes-utils/handle-authorized-data.ts';
import { sanitizeRedirectParam } from '@/routes-utils/sanitize-redirect-param.ts';
import { authCard, authContent, welcomeCard } from '../_auth/page.css.ts';
import { SeoHandle } from '../sitemap[.]xml/generate-sitemap.ts';
import { ChooseUsernameFallback } from './choose-username/choose-username-fallback.tsx';
import { ChooseUsername } from './choose-username/choose-username.tsx';
import { ConfirmEmailForm } from './confirm-email-form.tsx';
import { SetupMFAForm } from './setup-mfa-form.tsx';
import { SignupForm } from './signup-form.tsx';
import { signupStepVar } from './signup-step-var.ts';

export const handle: SeoHandle = {
  sitemap: true,
};

export const meta = () =>
  getPageMeta({
    url: 'https://synoptic.com/auth/signup',
  });

export const action = () => new Response(null, { status: 405 });

export default function Signup() {
  const [preferredUsername, setPreferredUsername] = useState<string>();
  const signupStep = useReactiveVar(signupStepVar);
  const location = useLocation();

  const [username, setUsername] = useState<string>(location.state?.username);
  const [email, setEmail] = useState<string>(location.state?.email);

  const params = new URLSearchParams(location.search);
  const fromParam = params.get('from');
  const to = sanitizeRedirectParam(fromParam);
  const navigate = useNavigate();
  const inviteCodeQueryParam = params.get('code');

  const handleSignup = (username: string, email: string) => {
    setUsername(username);
    setEmail(email);
    signupStepVar('confirmEmail');
  };

  const handleConfirmEmail = () => {
    signupStepVar('chooseUsername');
  };

  const handleChooseUsername = (newUsername: string) => {
    invalidateSession();
    setPreferredUsername(newUsername);
    signupStepVar('setupMFA');
  };

  const handleSetupMFA = (mfaAction: 'submit' | 'skip') => {
    navigate(to, { state: { afterAuth: true, mfaAction } });
  };

  return (
    <>
      <LayoutCard className={clsx(authCard, welcomeCard)}>
        <div className={authContent}>
          {signupStep === 'signup' && (
            <SignupForm
              onSignup={handleSignup}
              inviteCodeQueryParam={inviteCodeQueryParam}
            />
          )}
          {signupStep === 'confirmEmail' && (
            <ConfirmEmailForm
              email={email as string}
              username={username as string}
              onConfirmEmail={handleConfirmEmail}
            />
          )}
          {signupStep === 'chooseUsername' && (
            <Suspense fallback={<ChooseUsernameFallback />}>
              <ChooseUsername onChooseUsername={handleChooseUsername} />
            </Suspense>
          )}
          {signupStep === 'setupMFA' && (
            <SetupMFAForm
              username={preferredUsername as string}
              onSetupMFA={() => handleSetupMFA('submit')}
              onSkip={() => handleSetupMFA('skip')}
            />
          )}
        </div>
      </LayoutCard>
      <PrefetchPageLinks page={to} />
    </>
  );
}
